// Add agent modal — dynamic fields per agent type.

import { useState } from "react";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";

const S = "mt-1 block w-full border border-slate-200 rounded-lg px-3 py-2 text-sm";
const L = "block text-xs text-slate-500 mb-0.5";

const LANGUAGES = ["English", "Hindi", "Tamil", "Telugu", "Kannada", "Malayalam", "Bengali", "Gujarati", "Marathi"] as const;
const SPECIALIZATIONS = ["diabetes", "weight_management", "clinical_nutrition", "sports_nutrition", "pediatric_nutrition"] as const;
const PM_SERVICES = ["routine_inspection", "maintenance", "tenant_management", "rental_collection", "legal_documentation", "bill_payments"] as const;

interface Props {
  agentType: string;
  onClose: () => void;
  onCreated: () => void;
}

function CkGroup({ opts, val, set }: { opts: readonly string[]; val: string[]; set: (v: string[]) => void }): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-1">
      {opts.map((o) => (
        <label key={o} className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none capitalize">
          <input type="checkbox" className="rounded" checked={val.includes(o)}
            onChange={(e) => set(e.target.checked ? [...val, o] : val.filter((x) => x !== o))} />
          {o.replace(/_/g, " ")}
        </label>
      ))}
    </div>
  );
}

// eslint-disable-next-line max-lines-per-function, complexity -- single-form modal; per-branch extraction would shuffle lines without semantic gain. Tracked under TASK-N19 Phase B.
export function AddAgentModal({ agentType, onClose, onCreated }: Props): JSX.Element {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [labPartner, setLabPartner] = useState("");
  const [languages, setLanguages] = useState<string[]>([]);
  const [specializations, setSpecializations] = useState<string[]>([]);
  const [citiesCovered, setCitiesCovered] = useState("");
  const [services, setServices] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = name.trim().length > 0 && phone.trim().length > 0;

  async function submit(): Promise<void> {
    setSaving(true); setError(null);
    const body: Record<string, unknown> = { agent_type: agentType, name: name.trim(), phone: phone.trim() };
    if (agentType === "delivery_agent") { body.city = city; body.pincode = pincode; }
    if (agentType === "sample_collection_agent") { body.city = city; body.lab_partner = labPartner; }
    if (agentType === "nutritionist_agent") { body.languages = languages; body.specializations = specializations; }
    if (agentType === "property_manager") {
      body.cities_covered = citiesCovered.split(",").map((s) => s.trim()).filter(Boolean);
      body.services = services;
    }
    try {
      await apiFetch("/agents", { method: "POST", body: JSON.stringify(body) });
      onCreated();
    } catch { setError("Failed to save. Please try again."); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 py-8 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4 my-auto">
        <h2 className="text-lg font-semibold text-slate-900">Add Agent</h2>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <label className="block"><span className={L}>Name <span className="text-red-500">*</span></span>
            <input className={S} value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label className="block"><span className={L}>Phone <span className="text-red-500">*</span></span>
            <input className={S} value={phone} onChange={(e) => setPhone(e.target.value)} /></label>

          {(agentType === "delivery_agent" || agentType === "sample_collection_agent") && (
            <label className="block"><span className={L}>City</span>
              <input className={S} value={city} onChange={(e) => setCity(e.target.value)} /></label>
          )}
          {agentType === "delivery_agent" && (
            <label className="block"><span className={L}>Pincode</span>
              <input className={S} value={pincode} onChange={(e) => setPincode(e.target.value)} /></label>
          )}
          {agentType === "sample_collection_agent" && (
            <label className="block"><span className={L}>Lab partner</span>
              <input className={S} value={labPartner} onChange={(e) => setLabPartner(e.target.value)} /></label>
          )}
          {agentType === "nutritionist_agent" && (
            <>
              <div><span className={L}>Languages</span>
                <CkGroup opts={LANGUAGES} val={languages} set={setLanguages} /></div>
              <div><span className={L}>Specializations</span>
                <CkGroup opts={SPECIALIZATIONS} val={specializations} set={setSpecializations} /></div>
            </>
          )}
          {agentType === "property_manager" && (
            <>
              <label className="block"><span className={L}>Cities covered (comma-separated)</span>
                <input className={S} value={citiesCovered} onChange={(e) => setCitiesCovered(e.target.value)} /></label>
              <div><span className={L}>Services</span>
                <CkGroup opts={PM_SERVICES} val={services} set={setServices} /></div>
            </>
          )}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">Cancel</button>
          <button disabled={saving || !canSave} onClick={() => void submit()}
            className="px-4 py-2 text-sm rounded-lg bg-navy-700 text-white font-medium hover:bg-navy-800 disabled:opacity-50">
            {saving ? "Saving…" : "Add Agent"}
          </button>
        </div>
      </div>
    </div>
  );
}
